import { Component, inject, OnInit, OnDestroy, signal, computed, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, Subject, takeUntil } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DynamicFilterConfig, DynamicFilterValues, EnumValues, FilterOption, createDynamicFilterState } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { PaginatorState } from 'primeng/paginator';

import { EnumService } from '../../../services/enum.service';
import { SecondQualityDispatchProxyService } from '../../../proxies/second-quality-dispatch.proxy.service';
import { SecondQualityDispatch, SecondQualityDispatchStatus } from '../../../models/second-quality-dispatch/second-quality-dispatch';
import { SecondQualityDispatchSearchCommand } from '../../../models/second-quality-dispatch/second-quality-dispatch-commands';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-second-quality-dispatch-list',
  templateUrl: './second-quality-dispatch-list.component.html',
  styleUrls: ['./second-quality-dispatch-list.component.scss'],
})
export class SecondQualityDispatchListComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly enumService = inject(EnumService);
  private readonly translateService = inject(TranslateService);
  private readonly proxyService = inject(SecondQualityDispatchProxyService);

  private readonly destroy$ = new Subject<void>();
  private appliedFilters: DynamicFilterValues = {};

  // State signals
  isLoading = signal<number>(0);
  showFilters = signal<boolean>(true);
  dispatches = signal<SecondQualityDispatch[]>([]);
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({ storageKey: 'second-quality-dispatch-filters', config: this.filterConfig });

  // Enums
  sqdStatuses = signal<EnumValues>({});
  shipmentTypes = signal<EnumValues>({});
  enumsReady = signal(false);

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;

  // Computed: Status options for filter
  statusOptions = computed<FilterOption[]>(() => {
    const statuses = this.sqdStatuses();
    return Object.keys(statuses)
      .filter((key) => !isNaN(Number(key)))
      .map((key) => ({
        value: Number(key),
        label: statuses[Number(key)],
      }));
  });

  // Role-based permissions
  canCreate = computed(() => {
    return AuthService.hasRole(UserGroup.SecondQualityDispatchEditor) || AuthService.hasRole(UserGroup.SecondQualityDispatchAdmin);
  });

  constructor() {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    // Load enum values
    effect(
      () => {
        const statusesValue = this.enumService.getEnum('SecondQualityDispatchStatus', 'inventory').values;
        const shipmentTypesValue = this.enumService.getEnum('ShipmentType', 'order').values;
        if (Object.keys(statusesValue).length > 0) {
          this.sqdStatuses.set(statusesValue);
          this.shipmentTypes.set(shipmentTypesValue);
          this.enumsReady.set(true);
        }
      },
      { allowSignalWrites: true },
    );

    // Initialize filter config when language and enums are ready
    effect(
      () => {
        if (this.translateService.currentLang && this.enumsReady()) {
          this.tryInitializeFilterConfig();
        }
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.tryInitializeFilterConfig();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private tryInitializeFilterConfig(): void {
    if (this.translateService.currentLang && this.enumsReady()) {
      this.initializeFilterConfig();
    }
  }

  private initializeFilterConfig(): void {
    const translate = (key: string, fallback: string) => {
      const translation = this.translateService.instant(key);
      return translation === key ? fallback : translation;
    };

    const config: DynamicFilterConfig = {
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'number',
          label: translate('Number', 'Number'),
          placeholder: '',
          defaultVisible: true,
        },
        {
          type: 'text',
          formControlName: 'invoiceNumber',
          label: translate('SQD_Invoice_Number', 'Invoice Number'),
          defaultVisible: false,
          placeholder: '',
        },
        {
          type: 'multiselect',
          formControlName: 'statuses',
          label: translate('SQD_Status', 'Status'),
          options: this.statusOptions(),
          showClear: true,
          defaultVisible: true,
          placeholder: '',
        },
      ],
    };

    this.filterConfig.set(config);
  }

  private startLoading(): void {
    this.isLoading.update((val) => val + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((val) => val - 1);
  }

  search(): void {
    const formValues = this.appliedFilters;

    const number = (formValues['number'] as string | undefined)?.trim();
    const invoiceNumber = (formValues['invoiceNumber'] as string | undefined)?.trim();

    const statusesRaw = formValues['statuses'];
    const statuses = Array.isArray(statusesRaw)
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        statusesRaw.map((s: any) => (typeof s === 'object' && s.value !== undefined ? s.value : s))
      : [];

    const command: SecondQualityDispatchSearchCommand = {
      number: number || undefined,
      invoiceNumber: invoiceNumber || undefined,
      statuses: statuses && statuses.length > 0 ? statuses : undefined,
    };

    this.startLoading();
    this.proxyService
      .searchPaginated(command, this.currentPage, this.pageSize)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (result) => {
          const items = result?.data?.items || [];
          this.dispatches.set(items);
          this.totalRecords = result?.data?.totalCount || 0;
        },
        error: () => {
          this.dispatches.set([]);
          this.totalRecords = 0;
        },
      });
  }

  onPageChange(event: PaginatorState): void {
    this.currentPage = (event.page || 0) + 1;
    this.pageSize = event.rows || this.pageSize;
    this.search();
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    this.appliedFilters = filters;
    this.currentPage = 1;
    this.search();
  }

  redirectToDetail(id?: string): void {
    const queryParams = id ? { id } : { mode: 'create' };
    this.router.navigate(['/second-quality-dispatch-details'], { queryParams });
  }

  createNew(): void {
    this.router.navigate(['/second-quality-dispatch-details'], { queryParams: { mode: 'create' } });
  }

  getStatusLabel(status: SecondQualityDispatchStatus): string {
    const statuses = this.sqdStatuses();
    return statuses[status] || status.toString();
  }

  getStatusSeverity(status: SecondQualityDispatchStatus): 'success' | 'info' | 'warn' | 'secondary' | 'contrast' | 'danger' | undefined {
    switch (status) {
      case SecondQualityDispatchStatus.Draft:
        return 'secondary';
      case SecondQualityDispatchStatus.Completed:
        return 'success';
      case SecondQualityDispatchStatus.Cancelled:
        return 'danger';
      default:
        return 'secondary';
    }
  }

  getShipmentTypeLabel(shipmentType: number): string {
    const types = this.shipmentTypes();
    return types[shipmentType] || shipmentType.toString();
  }
}
